import {
  classNames,
  React,
  useEffect,
  useState,
  useRef,
} from '@digsup/core-ui';
import PropTypes from 'prop-types';
import {
  Field as FormikField,
  FastField as FormikFastField,
  useFormikContext,
} from 'formik';
import { Cell, Grid } from '@digsup/components-grid';
import { Text } from '@digsup/components-text';
import { Icon } from '@digsup/components-icon';
import { Popover } from '@digsup/components-popover';
import { FormContext } from './form-context';
import { styles } from './Field.scss';
import { useFocusOnError } from './use-focus-on-error';

const cx = classNames.bind(styles);

const formikFieldType = fast => {
  if (!fast) {
    return FormikFastField;
  }
  return FormikField;
};

const renderHelper = helper => {
  if (helper) {
    if (React.isValidElement(helper)) {
      return helper;
    }
    return (
      <Text className="u-margin-right-small u-margin-top-xsmall">{helper}</Text>
    );
  }
  return null;
};

export const Field = ({
  children,
  name,
  independent,
  label,
  helper,
  htmlFor,
  tooltip,
  ...props
}) => {
  const FieldComponent = formikFieldType(independent);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const { status, setStatus } = useFormikContext();
  const errorIconRef = useRef();
  const inputRef = useRef();
  useFocusOnError({ fieldRef: inputRef, name });

  useEffect(
    () => setShowErrorMessage(status?.focused === name),
    [status?.focused],
  );

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        errorIconRef &&
        errorIconRef.current &&
        !errorIconRef.current.contains(e.target) &&
        e.target.name !== name &&
        status?.focused === name
      ) {
        setStatus({ focused: '' });
        e.stopPropagation();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  useEffect(() => {
    const handleFocusInput = () => {
      if (status !== name) {
        setStatus({ focused: name });
      }
    };
    // eslint-disable-next-line no-unused-expressions
    window.addEventListener?.('input-focus', handleFocusInput);

    // eslint-disable-next-line no-unused-expressions
    inputRef.current?.addEventListener?.('focus', handleFocusInput);
    // eslint-disable-next-line no-unused-expressions
    errorIconRef.current?.addEventListener?.('click', handleFocusInput);

    return () => {
      // eslint-disable-next-line no-unused-expressions
      window.removeEventListener?.('input-focus', handleFocusInput);

      // eslint-disable-next-line no-unused-expressions
      inputRef.current?.removeEventListener?.('focus', handleFocusInput);
      // eslint-disable-next-line no-unused-expressions
      errorIconRef.current?.removeEventListener?.('click', handleFocusInput);
    };
  }, [inputRef.current, errorIconRef.current]);
  return (
    <FieldComponent name={name} {...props}>
      {formikProps => {
        const { field, meta, form } = formikProps;
        return (
          <FormContext.Consumer>
            {formContext => {
              const fieldProps = { ...formContext.fieldProps, ...props };

              const {
                hoverBorder,
                className,
                grid,
                controlClassName,
                labelClassName,
                isInlineEditable,
              } = fieldProps;

              const classes = cx('c-form-field', {
                [`${className}`]: !!className,
                'c-form-field--hover-border': hoverBorder,
                'c-form-field--inline-editable': !!isInlineEditable,
              });

              return (
                <Grid gutter="none" {...grid} className={classes}>
                  {(label || helper) && (
                    <Cell {...grid?.label} className={labelClassName}>
                      {label && (
                        <Text
                          className="u-margin-right-small"
                          tag="label"
                          marginBottom="none"
                          fontWeight="semibold"
                          htmlFor={htmlFor || field?.name}>
                          {label}
                        </Text>
                      )}
                      {renderHelper(helper)}
                    </Cell>
                  )}
                  <Cell {...grid?.control} className={controlClassName}>
                    <div className="c-form-field__wrapper">
                      {!!meta?.error && !!meta?.touched && (
                        <div
                          className="c-form-field__error-icon"
                          id={`field-${name}`}>
                          <Popover
                            zIndex="modal"
                            trigger="none"
                            theme="danger"
                            placement="top-start"
                            tabIndex="-1"
                            referenceOffset={7}
                            renderer={() => (
                              <div className="c-form-field__popover">
                                <Text color="theme-body">{meta?.error}</Text>
                              </div>
                            )}
                            tooltipShown={showErrorMessage}
                            {...formContext.fieldProps?.tooltip}
                            {...tooltip}>
                            <Icon
                              style={{ cursor: 'pointer' }}
                              ref={errorIconRef}
                              name="information"
                              shape="round"
                              fill="white"
                              shapeColor="tenacious"
                            />
                          </Popover>
                        </div>
                      )}
                      {children({
                        field: {
                          ...field,
                          ref: inputRef,
                          id: field.name,
                          hasError: !!meta.error,
                        },
                        meta,
                        form,
                      })}
                    </div>
                  </Cell>
                </Grid>
              );
            }}
          </FormContext.Consumer>
        );
      }}
    </FieldComponent>
  );
};

Field.propTypes = {
  className: PropTypes.string,
  label: PropTypes.string,
  helper: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  htmlFor: PropTypes.string,
  children: PropTypes.any,
  independent: PropTypes.bool,
  tooltip: PropTypes.object,
  grid: PropTypes.object,
  name: PropTypes.string.isRequired,
};

Field.defaultProps = {
  label: '',
  helper: '',
  children: null,
  disableTooltip: false,
  zIndexTooltip: 'page',
  independent: true,
  hoverBorder: false,
  name: '',
};
